import { z } from 'zod'

/**
 * Offer notification template requests (TO-140). SHAPE ONLY — types and strict
 * keys. The field bounds (REQ-001/002) are checked by offer-service on the merged
 * template, so an out-of-bounds value is answered with the coded 422
 * `NOTIFICATION_TEMPLATE_INVALID` listing every offending field, not a generic
 * `VALIDATION_ERROR`.
 */

const templateFields = {
	name: z.string(),
	description: z.string().nullable(),
	channelType: z.string(),
	locale: z.string(),
	subject: z.string().nullable(),
	body: z.string(),
	isDefaultForChannel: z.boolean(),
	isActive: z.boolean(),
}

/** `POST /offers/templates`. */
export const notificationTemplateCreateSchema = z
	.object({
		name: templateFields.name,
		description: templateFields.description.optional(),
		channelType: templateFields.channelType,
		locale: templateFields.locale,
		subject: templateFields.subject.optional(),
		body: templateFields.body,
		isDefaultForChannel: templateFields.isDefaultForChannel.optional(),
		isActive: templateFields.isActive.optional(),
	})
	.strict()

/** `PATCH /offers/templates/{id}`: any non-empty subset of the create fields. */
export const notificationTemplatePatchSchema = z
	.object({
		name: templateFields.name.optional(),
		description: templateFields.description.optional(),
		channelType: templateFields.channelType.optional(),
		locale: templateFields.locale.optional(),
		subject: templateFields.subject.optional(),
		body: templateFields.body.optional(),
		isDefaultForChannel: templateFields.isDefaultForChannel.optional(),
		isActive: templateFields.isActive.optional(),
	})
	.strict()
	.refine((value) => Object.keys(value).length > 0, { message: 'at least one field is required' })

/** The longest subject or body a preview renders (the template bound). */
export const NOTIFICATION_TEMPLATE_PREVIEW_MAX = 2000

/** `POST /offers/templates/preview`: draft text rendered against one of the caller's offers. */
export const notificationTemplatePreviewSchema = z
	.object({
		offerId: z.uuid(),
		subject: z.string().max(NOTIFICATION_TEMPLATE_PREVIEW_MAX).nullable().optional(),
		body: z.string().min(1).max(NOTIFICATION_TEMPLATE_PREVIEW_MAX),
	})
	.strict()

export type NotificationTemplateCreateInput = z.infer<typeof notificationTemplateCreateSchema>
export type NotificationTemplatePatchInput = z.infer<typeof notificationTemplatePatchSchema>
export type NotificationTemplatePreviewInput = z.infer<typeof notificationTemplatePreviewSchema>
